import { useCallback, useRef, useState } from 'react';

/**
 * Tracks tap count, total tapping duration and the gaps between taps during
 * the charging phase. The backend derives the authoritative desperation level
 * from these numbers (backend/lib/orbEngine.js); `intensity` here is only for
 * the live visual escalation of the orb.
 */
export function useTapTracker() {
  const [tapCount, setTapCount] = useState(0);
  const firstTapAt = useRef(null);
  const lastTapAt = useRef(null);
  const intervals = useRef([]);

  const registerTap = useCallback(() => {
    const now = Date.now();
    if (firstTapAt.current == null) firstTapAt.current = now;
    if (lastTapAt.current != null) intervals.current.push(now - lastTapAt.current);
    lastTapAt.current = now;
    setTapCount((c) => c + 1);
  }, []);

  const getStats = useCallback(() => ({
    tapCount,
    tapDurationMs: firstTapAt.current != null && lastTapAt.current != null
      ? Math.max(0, lastTapAt.current - firstTapAt.current)
      : 0,
    tapIntervalsMs: intervals.current.slice(0, 200),
  }), [tapCount]);

  const reset = useCallback(() => {
    firstTapAt.current = null;
    lastTapAt.current = null;
    intervals.current = [];
    setTapCount(0);
  }, []);

  // 0–4, saturating at 20 taps — drives the orb's shake/glow escalation.
  const intensity = Math.min(4, Math.floor(tapCount / 5));

  return { tapCount, intensity, registerTap, getStats, reset };
}
